import { textoDasDatasDoSubPedido } from '../../pedidos/datasDoSubPedido'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import type { SubPedido } from '../../db/tipos'
import { formatarMoeda } from '../../lib/formatos'
import { cancelarSubPedido, mudarStatusSubPedido } from '../../pedidos/fechamento'
import { MOTIVOS_AJUSTE, rotuloDoMotivo } from '../../pedidos/ajustes'
import { CLASSE_ESTAGIO, ROTULO_ESTAGIO } from '../../pedidos/estagio'
import AjustarItens from './AjustarItens'
import ConfirmarEntrega from './ConfirmarEntrega'
import ItensDoSubPedido from './ItensDoSubPedido'

export default function DetalhePedido() {
  const { id } = useParams()
  const pedido = useLiveQuery(() => buscarVivo(banco.pedidos, id), [id])
  const cliente = useLiveQuery(
    () => (pedido ? banco.clientes.get(pedido.cliente_id) : undefined),
    [pedido?.cliente_id],
  )
  const representadas = useLiveQuery(() => banco.representadas.toArray(), [])
  const subPedidos = useLiveQuery(
    () =>
      id
        ? banco.sub_pedidos
            .where('pedido_id')
            .equals(id)
            .filter((s) => s.apagado_em === null)
            .sortBy('letra')
        : Promise.resolve<SubPedido[]>([]),
    [id],
  )

  const [ajustando, setAjustando] = useState<string | null>(null)
  const [vendo, setVendo] = useState<string | null>(null)
  const [confirmandoEntrega, setConfirmandoEntrega] = useState<string | null>(null)
  const [cancelando, setCancelando] = useState<string | null>(null)
  const [motivoCancelamento, setMotivoCancelamento] = useState('')

  const nomeRepresentada = (idRep: string) =>
    representadas?.find((r) => r.id === idRep)?.nome ?? ''

  // Guardas de transição podem lançar se uma sincronização mudou o status entre
  // o render e o clique. Aqui a rejeição vira aviso ao usuário, não erro silencioso.
  async function transicionar(acao: () => Promise<void>) {
    try {
      await acao()
    } catch (erro) {
      window.alert(
        (erro instanceof Error ? `${erro.message}\n\n` : '') +
          'O pedido pode ter sido atualizado por uma sincronização. Recarregue e tente de novo.',
      )
    }
  }

  // O valor faturado deixou de ser digitado: depois de "Ajustar itens" o total
  // do sub-pedido JA e o que a fabrica faturou. Perguntar de novo seria a mesma
  // informacao por dois caminhos que podem discordar -- e a comissao seguiria o
  // numero digitado, nao os itens. O campo valor_faturado continua na tabela e
  // continua respeitado nos pedidos antigos que ja tem valor la.
  async function marcarFaturado(subPedido: SubPedido) {
    await transicionar(() => mudarStatusSubPedido(subPedido, 'faturado'))
  }

  // Texto livre virou lista fechada: cancelamento e a maior perda de venda de
  // uma vez so, e sem codigo ele ficaria fora da metrica. O par "registra a
  // perda + muda o status" mora em cancelarSubPedido, compartilhado com a
  // "nota cancelada" da conferencia da entrega.
  async function confirmarCancelamento(subPedido: SubPedido, motivo: string) {
    await transicionar(() => cancelarSubPedido(subPedido, motivo))
    setCancelando(null)
    setMotivoCancelamento('')
  }

  if (!pedido) return <p className="p-4 text-sm text-slate-500">Carregando…</p>

  return (
    <section className="p-4 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Pedido {pedido.numero}</h2>
        <p className="text-sm text-slate-500">{cliente?.nome_fantasia || cliente?.razao_social}</p>
      </div>

      {subPedidos?.map((subPedido) => (
        <div key={subPedido.id} className="bg-white rounded-xl shadow-sm p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-slate-800">
              {subPedido.letra} — {nomeRepresentada(subPedido.representada_id)}
            </h3>
            {/* As mesmas cores da lista de pedidos: as duas telas falam a
                mesma lingua sobre onde a venda esta. */}
            <span className={`text-xs rounded px-2 py-0.5 ${CLASSE_ESTAGIO[subPedido.status]}`}>
              {ROTULO_ESTAGIO[subPedido.status]}
            </span>
          </div>
          {textoDasDatasDoSubPedido(subPedido) && (
            <p className="text-xs text-slate-500">{textoDasDatasDoSubPedido(subPedido)}</p>
          )}
          <p className="text-sm text-slate-700">
            Total: <strong>{formatarMoeda(subPedido.valor_total)}</strong>
            {subPedido.valor_faturado !== null && (
              <> — Faturado: <strong>{formatarMoeda(subPedido.valor_faturado)}</strong></>
            )}
          </p>
          {subPedido.motivo_cancelamento && (
            // rotuloDoMotivo devolve o proprio texto quando o codigo e
            // desconhecido: cancelamentos anteriores a este ciclo gravaram texto
            // livre, e continuam aparecendo como foram escritos.
            <p className="text-xs text-red-600">
              Cancelado: {rotuloDoMotivo(subPedido.motivo_cancelamento)}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {/* Em QUALQUER estado: depois de faturado nao havia como ver os
                itens, porque a lista morava dentro de "Ajustar itens" e sumia
                junto com ele (achado dela em 2026-07-30). */}
            <button
              onClick={() => setVendo(vendo === subPedido.id ? null : subPedido.id)}
              className="text-xs border border-slate-300 text-slate-600 rounded px-2 py-1"
            >
              {vendo === subPedido.id ? 'Fechar itens' : 'Ver itens'}
            </button>
            {subPedido.status === 'enviado' && (
              <button
                onClick={() => void marcarFaturado(subPedido)}
                className="text-xs bg-teal-700 text-white rounded px-2 py-1"
              >
                Marcar faturado
              </button>
            )}
            {/* Editavel ate faturar: e a janela em que o vendedor reflete o
                corte da fabrica antes de congelar. */}
            {(subPedido.status === 'fechado' || subPedido.status === 'enviado') && (
              <button
                onClick={() => setAjustando(ajustando === subPedido.id ? null : subPedido.id)}
                className="text-xs border border-teal-700 text-teal-700 rounded px-2 py-1"
              >
                Ajustar itens
              </button>
            )}
            {/* Faturado passou a ter UMA saida: a conferencia da entrega. O
                "Cliente pagou" saiu daqui (pagamento e o ultimo passo do ciclo)
                e o "Cancelar" tambem -- cancelar nota ja emitida nao existe; o
                que existe e "nota cancelada", dentro da conferencia. */}
            {subPedido.status === 'faturado' && (
              <button
                onClick={() => {
                  // A conferencia ja traz a lista: manter o "Ver itens" aberto
                  // mostraria a mesma tabela duas vezes.
                  setVendo(null)
                  setConfirmandoEntrega(confirmandoEntrega === subPedido.id ? null : subPedido.id)
                }}
                className="text-xs bg-teal-700 text-white rounded px-2 py-1"
              >
                Confirma entrega
              </button>
            )}
            {subPedido.status === 'entregue' && (
              <button
                onClick={() => void transicionar(() => mudarStatusSubPedido(subPedido, 'pago'))}
                className="text-xs bg-teal-700 text-white rounded px-2 py-1"
              >
                Cliente pagou
              </button>
            )}
            {/* Cancelar so enquanto a nota nao saiu. */}
            {(subPedido.status === 'fechado' || subPedido.status === 'enviado') && (
              <button
                onClick={() => {
                  setCancelando(cancelando === subPedido.id ? null : subPedido.id)
                  setMotivoCancelamento('')
                }}
                className="text-xs border border-red-300 text-red-600 rounded px-2 py-1"
              >
                Cancelar
              </button>
            )}
          </div>

          {cancelando === subPedido.id && (
            <div className="bg-white border border-red-200 rounded-xl p-3 space-y-2">
              <p className="text-sm text-slate-700">
                Cancelando {subPedido.letra} — {nomeRepresentada(subPedido.representada_id)} (
                {formatarMoeda(subPedido.valor_total)}). Por quê?
              </p>
              {MOTIVOS_AJUSTE.map((m) => (
                <label key={m.codigo} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="radio"
                    name={`motivo-cancelamento-${subPedido.id}`}
                    checked={motivoCancelamento === m.codigo}
                    onChange={() => setMotivoCancelamento(m.codigo)}
                    aria-label={m.rotulo}
                  />
                  {m.rotulo}
                </label>
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!motivoCancelamento}
                  onClick={() => void confirmarCancelamento(subPedido, motivoCancelamento)}
                  className="bg-red-600 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={() => setCancelando(null)}
                  className="text-slate-600 text-sm underline"
                >
                  Voltar
                </button>
              </div>
            </div>
          )}

          {vendo === subPedido.id && <ItensDoSubPedido subPedido={subPedido} />}

          {ajustando === subPedido.id && (
            <AjustarItens subPedido={subPedido} aoFechar={() => setAjustando(null)} />
          )}

          {/* O status entra na condicao junto com o id: devolver tudo cancela o
              sub-pedido, e o bloco tem de sumir sozinho quando isso acontece. */}
          {confirmandoEntrega === subPedido.id && subPedido.status === 'faturado' && (
            <ConfirmarEntrega
              subPedido={subPedido}
              aoFechar={() => setConfirmandoEntrega(null)}
            />
          )}
        </div>
      ))}

      {/* Pedido dela 14/10: produto de OUTRA tabela ou fornecedora entra como
          sub-pedido novo, sem mexer no que ja foi enviado. O novo nasce em
          rascunho e precisa ser fechado para ganhar o PDF dele. */}
      {pedido.status === 'fechado' && (subPedidos ?? []).some((s) => s.status === 'rascunho') && (
        <Link
          to={`/pedidos/${pedido.id}/fechar`}
          className="block text-center bg-teal-700 text-white rounded-lg py-2 font-medium"
        >
          Fechar o sub-pedido novo
        </Link>
      )}
      {pedido.status === 'fechado' && (
        <Link
          to={`/pedidos/${pedido.id}/acrescentar`}
          className="block text-center border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
        >
          ＋ Acrescentar produto de outra tabela ou fornecedora
        </Link>
      )}

      {pedido.status === 'fechado' && (
        <Link
          to={`/pedidos/${pedido.id}/enviar`}
          className="block text-center border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
        >
          Abrir PDFs / reenviar
        </Link>
      )}
    </section>
  )
}
